import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Copy, CheckCheck, ExternalLink, HardDrive, User, Key, FileText } from 'lucide-react';

const CopyBtn = ({ value }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={copy} className="p-0.5 text-[#F0EFF4]/30 hover:text-[#7B61FF] transition-colors">
      {copied ? <CheckCheck className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
    </button>
  );
};

const MaskedField = ({ value, label }) => {
  const [revealed, setRevealed] = useState(false);
  if (!value) return <span className="text-[#F0EFF4]/20 italic">—</span>;
  return (
    <span className="flex items-center gap-1 font-data text-xs">
      {revealed ? value : '••••••••'}
      <button onClick={() => setRevealed(v => !v)} className="text-[#F0EFF4]/30 hover:text-[#7B61FF] transition-colors">
        {revealed ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
      </button>
      {revealed && <CopyBtn value={value} />}
    </span>
  );
};

const Row = ({ label, value, mono }) => (
  <div className="flex items-start gap-2 py-1.5 border-b border-[#F0EFF4]/5 last:border-0">
    <span className="text-[11px] text-[#F0EFF4]/40 font-data w-24 shrink-0 pt-0.5">{label}</span>
    <span className={`text-sm text-[#F0EFF4] flex-1 ${mono ? 'font-data' : ''}`}>{value || '—'}</span>
  </div>
);

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-xl p-4 mb-3">
    <div className="flex items-center gap-2 mb-3">
      {Icon && <Icon className="w-3.5 h-3.5 text-[#7B61FF]" />}
      <h4 className="text-xs font-bold text-[#F0EFF4] uppercase tracking-wider font-data">{title}</h4>
    </div>
    {children}
  </div>
);

const BuildContextPanel = ({ build, intake, credentials, documents }) => {
  if (!build) return null;

  const client = build.clients || build.client;
  const hardware = build.hardware_inventory || build.hardware;

  return (
    <div className="h-full overflow-y-auto pr-1 space-y-0">
      {/* Client */}
      <Section title="Client" icon={User}>
        <Row label="Name" value={client?.name} />
        <Row label="Company" value={client?.company} />
        <Row label="Email" value={client?.email} mono />
        {client?.phone && <Row label="Phone" value={client.phone} />}
        <div className="mt-2">
          <Link
            to={`/admin/client/${client?.id}`}
            className="inline-flex items-center gap-1 text-xs text-[#7B61FF] hover:text-[#6B51EF] font-data"
          >
            Open Dossier <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </Section>

      {/* Hardware */}
      <Section title="Hardware" icon={HardDrive}>
        {hardware ? (
          <>
            <Row label="Label" value={hardware.label} />
            <Row label="Model" value={hardware.model} />
            <Row label="Serial" value={hardware.serial_number} mono />
          </>
        ) : (
          <p className="text-xs text-[#F0EFF4]/30 italic">No hardware assigned</p>
        )}
        {build.filevault_key && (
          <div className="mt-2 pt-2 border-t border-[#F0EFF4]/5">
            <div className="flex items-start gap-2">
              <span className="text-[11px] text-[#F0EFF4]/40 font-data w-24 shrink-0 pt-0.5">FileVault</span>
              <MaskedField value={build.filevault_key} label="FileVault Key" />
            </div>
          </div>
        )}
      </Section>

      {/* Intake Highlights */}
      {intake && (
        <Section title="Intake" icon={FileText}>
          {Object.entries(intake)
            .filter(([k]) => !['id','client_id','created_at','updated_at'].includes(k))
            .slice(0, 6)
            .map(([k, v]) => (
              <Row key={k} label={k.replace(/_/g, ' ')} value={typeof v === 'object' ? JSON.stringify(v) : String(v ?? '—')} />
            ))}
        </Section>
      )}

      {/* Credentials */}
      {credentials?.length > 0 && (
        <Section title="Credentials" icon={Key}>
          {credentials.map(c => (
            <div key={c.id} className="mb-3 last:mb-0">
              <div className="text-xs font-bold text-[#F0EFF4] mb-1">{c.integration_name}</div>
              {c.username && <Row label="User" value={c.username} mono />}
              {c.password && (
                <div className="flex items-start gap-2 py-1.5 border-b border-[#F0EFF4]/5">
                  <span className="text-[11px] text-[#F0EFF4]/40 font-data w-24 shrink-0 pt-0.5">Password</span>
                  <MaskedField value={c.password} label="Password" />
                </div>
              )}
              {c.api_key && (
                <div className="flex items-start gap-2 py-1.5 border-b border-[#F0EFF4]/5">
                  <span className="text-[11px] text-[#F0EFF4]/40 font-data w-24 shrink-0 pt-0.5">API Key</span>
                  <MaskedField value={c.api_key} label="API Key" />
                </div>
              )}
            </div>
          ))}
        </Section>
      )}

      {/* SOUL preview */}
      {build.soul_md_preview && (
        <Section title="SOUL.md Preview" icon={FileText}>
          <pre className="text-[11px] font-data text-[#F0EFF4]/60 whitespace-pre-wrap leading-relaxed max-h-40 overflow-y-auto">
            {build.soul_md_preview}
            {build.soul_md && build.soul_md.length > 500 && '\n…'}
          </pre>
        </Section>
      )}

      {/* Documents */}
      {documents?.length > 0 && (
        <Section title="Knowledge Base" icon={FileText}>
          {documents.slice(0, 5).map(doc => (
            <div key={doc.id} className="text-xs text-[#F0EFF4]/60 py-1 border-b border-[#F0EFF4]/5 last:border-0 truncate">
              {doc.filename || doc.description || 'Unnamed'}
            </div>
          ))}
          {documents.length > 5 && (
            <div className="text-xs text-[#F0EFF4]/30 pt-1">+{documents.length - 5} more</div>
          )}
        </Section>
      )}
    </div>
  );
};

export default BuildContextPanel;
